import { useI18nCommonFe } from "@solvari/translations";
import { arrayWrap } from "@solvari/utils";

import type {
  Decimals,
  PrimitiveOrArrayValue,
} from "@/lib/components/logic/atoms/input/props";

import { truncateDecimals } from "@/lib/helpers/numbers";
import { eager } from "@/lib/validation/events";
import { defineRule } from "@/lib/validation/rules/defineRule";

const number = defineRule(
  (value: unknown) => {
    return arrayWrap(value).every((value) => !Number.isNaN(Number(value)));
  },
  (locale) => {
    const { tr } = useI18nCommonFe({ locale });
    return {
      name: "number",
      events: eager,
      message: ({ messageParams }) =>
        tr("common_fe.validation.number", messageParams),
    };
  },
);

const decimals = defineRule(
  (value: PrimitiveOrArrayValue | null | undefined, params: Decimals) => {
    if (!value) {
      return true;
    }
    return arrayWrap(value).every(
      (value) =>
        typeof value !== "boolean" &&
        Number(truncateDecimals(value, params)) === Number(value),
    );
  },
  (locale) => {
    const { tr } = useI18nCommonFe({ locale });
    return {
      name: "decimals",
      events: eager,
      message: ({ messageParams }) =>
        tr("common_fe.validation.decimals", messageParams),
    };
  },
);

const min = defineRule(
  (value: PrimitiveOrArrayValue | null | undefined, params: number) => {
    const normalizedValue = value || 0;
    return arrayWrap(normalizedValue).every(
      (value) => typeof value !== "boolean" && Number(value) >= params,
    );
  },
  (locale) => {
    const { tr } = useI18nCommonFe({ locale });
    return {
      name: "min",
      message: ({ messageParams }) =>
        tr("common_fe.validation.min", messageParams),
    };
  },
);

const max = defineRule(
  (value: PrimitiveOrArrayValue | null | undefined, params: number) => {
    const normalizedValue = value || 0;
    return arrayWrap(normalizedValue).every(
      (value) => typeof value !== "boolean" && Number(value) <= params,
    );
  },
  (locale) => {
    const { tr } = useI18nCommonFe({ locale });
    return {
      name: "max",
      message: ({ messageParams }) =>
        tr("common_fe.validation.max", messageParams),
    };
  },
);

export { decimals, max, min, number };
